/**
 * PDF Export Utility for Financial Statements
 * Uses jsPDF with autoTable for professional financial document generation.
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const COLORS = {
  primary: [30, 64, 115] as [number, number, number],    // Dark navy
  secondary: [100, 116, 139] as [number, number, number], // Slate
  accent: [16, 185, 129] as [number, number, number],     // Emerald
  danger: [239, 68, 68] as [number, number, number],      // Red
  black: [0, 0, 0] as [number, number, number],
  gray: [100, 116, 139] as [number, number, number],
  lightGray: [241, 245, 249] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
};

function formatCurrency(value: number): string {
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (value < 0) return `($${formatted})`;
  return `$${formatted}`;
}

function addHeader(doc: jsPDF, title: string, subtitle: string, businessName?: string) {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Company name
  if (businessName) {
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.primary);
    doc.text(businessName, pageWidth / 2, 20, { align: 'center' });
  }

  // Statement title
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.black);
  doc.text(title, pageWidth / 2, businessName ? 30 : 20, { align: 'center' });

  // Period / date subtitle
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.gray);
  doc.text(subtitle, pageWidth / 2, businessName ? 37 : 27, { align: 'center' });

  // Divider line
  const y = businessName ? 42 : 32;
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(20, y, pageWidth - 20, y);

  return y + 8;
}

function addFooter(doc: jsPDF) {
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray);
    doc.text(
      `Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
      20,
      pageHeight - 10,
    );
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
  }
}

// ============================================
// Income Statement / P&L
// ============================================

export interface IncomeStatementData {
  period: { from: string; to: string };
  revenue: {
    gross: number;
    taxCollected: number;
    byType: { type: string; amount: number; invoiceCount: number }[];
  };
  costOfGoodsSold: number;
  grossProfit: number;
  operatingExpenses: {
    total: number;
    byCategory: { category: string; slug: string; amount: number; count: number }[];
    subscriptions: { total: number; count: number };
  };
  operatingIncome: number;
  netIncome: number;
  monthlyBreakdown: { month: string; monthName: string; revenue: number; expenses: number; netIncome: number }[];
}

export function exportIncomeStatementPDF(data: IncomeStatementData, businessName?: string): void {
  const doc = new jsPDF();
  const fromDate = new Date(data.period.from).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const toDate = new Date(data.period.to).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  let y = addHeader(doc, 'Income Statement', `For the Period ${fromDate} to ${toDate}`, businessName);

  // Revenue Section
  const revenueRows: (string | number)[][] = [];
  for (const r of data.revenue.byType) {
    const label = r.type.charAt(0).toUpperCase() + r.type.slice(1).replace('_', ' ');
    revenueRows.push([`  ${label} Revenue`, formatCurrency(r.amount)]);
  }
  revenueRows.push([{ content: 'Gross Revenue', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.revenue.gross), styles: { fontStyle: 'bold' } } as unknown as string]);

  autoTable(doc, {
    startY: y,
    head: [['Revenue', 'Amount']],
    body: revenueRows,
    theme: 'plain',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { fontSize: 9, cellPadding: 2 },
    margin: { left: 20, right: 20 },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;

  // COGS
  if (data.costOfGoodsSold > 0) {
    autoTable(doc, {
      startY: y,
      body: [
        ['Cost of Goods Sold', formatCurrency(data.costOfGoodsSold)],
        [{ content: 'Gross Profit', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.grossProfit), styles: { fontStyle: 'bold' } } as unknown as string],
      ],
      theme: 'plain',
      columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
      styles: { fontSize: 9, cellPadding: 2 },
      margin: { left: 20, right: 20 },
    });
    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;
  }

  // Operating Expenses
  const expenseRows: (string | number)[][] = [];
  for (const e of data.operatingExpenses.byCategory) {
    expenseRows.push([`  ${e.category}`, formatCurrency(e.amount)]);
  }
  if (data.operatingExpenses.subscriptions.total > 0) {
    expenseRows.push([`  Subscriptions (${data.operatingExpenses.subscriptions.count})`, formatCurrency(data.operatingExpenses.subscriptions.total)]);
  }
  expenseRows.push([{ content: 'Total Operating Expenses', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.operatingExpenses.total), styles: { fontStyle: 'bold' } } as unknown as string]);

  autoTable(doc, {
    startY: y,
    head: [['Operating Expenses', 'Amount']],
    body: expenseRows,
    theme: 'plain',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { fontSize: 9, cellPadding: 2 },
    margin: { left: 20, right: 20 },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;

  // Net Income Summary
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(1);
  doc.line(20, y, doc.internal.pageSize.getWidth() - 20, y);
  y += 2;

  autoTable(doc, {
    startY: y,
    body: [
      [
        { content: 'Net Income (Before Tax)', styles: { fontStyle: 'bold', fontSize: 11 } } as unknown as string,
        { content: formatCurrency(data.netIncome), styles: { fontStyle: 'bold', fontSize: 11, textColor: data.netIncome >= 0 ? COLORS.accent : COLORS.danger } } as unknown as string,
      ],
    ],
    theme: 'plain',
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { cellPadding: 3 },
    margin: { left: 20, right: 20 },
  });

  addFooter(doc);
  doc.save(`Income_Statement_${data.period.from}_to_${data.period.to}.pdf`);
}

// ============================================
// Balance Sheet
// ============================================

export interface BalanceSheetData {
  asOfDate: string;
  assets: {
    current: { accountsReceivable: number; outstandingInvoiceCount: number; totalCurrent: number };
    fixed: {
      byType: { type: string; count: number; cost: number; accumulatedDepreciation: number; netBookValue: number }[];
      totalCost: number;
      totalAccumulatedDepreciation: number;
      netFixedAssets: number;
    };
    totalAssets: number;
  };
  liabilities: {
    byType: { type: string; count: number; balance: number; monthlyPayment: number }[];
    totalLiabilities: number;
  };
  equity: { ownersEquity: number };
  totalLiabilitiesAndEquity: number;
}

export function exportBalanceSheetPDF(data: BalanceSheetData, businessName?: string): void {
  const doc = new jsPDF();
  const asOf = new Date(data.asOfDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  let y = addHeader(doc, 'Balance Sheet', `As of ${asOf}`, businessName);

  // Assets
  const assetRows: (string | number)[][] = [
    [{ content: 'Current Assets', styles: { fontStyle: 'bold' } } as unknown as string, ''],
    [`  Accounts Receivable (${data.assets.current.outstandingInvoiceCount} invoices)`, formatCurrency(data.assets.current.accountsReceivable)],
    [{ content: 'Total Current Assets', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.assets.current.totalCurrent), styles: { fontStyle: 'bold' } } as unknown as string],
    ['', ''],
    [{ content: 'Fixed Assets', styles: { fontStyle: 'bold' } } as unknown as string, ''],
  ];

  for (const a of data.assets.fixed.byType) {
    const label = a.type.charAt(0).toUpperCase() + a.type.slice(1).replace('_', ' ');
    assetRows.push([`  ${label} (${a.count})`, formatCurrency(a.netBookValue)]);
  }
  if (data.assets.fixed.totalAccumulatedDepreciation > 0) {
    assetRows.push(['  Less: Accumulated Depreciation', `(${formatCurrency(data.assets.fixed.totalAccumulatedDepreciation)})`]);
  }
  assetRows.push([{ content: 'Net Fixed Assets', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.assets.fixed.netFixedAssets), styles: { fontStyle: 'bold' } } as unknown as string]);
  assetRows.push(['', '']);
  assetRows.push([{ content: 'TOTAL ASSETS', styles: { fontStyle: 'bold', fontSize: 10 } } as unknown as string, { content: formatCurrency(data.assets.totalAssets), styles: { fontStyle: 'bold', fontSize: 10 } } as unknown as string]);

  autoTable(doc, {
    startY: y,
    head: [['Assets', 'Amount']],
    body: assetRows,
    theme: 'plain',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { fontSize: 9, cellPadding: 2 },
    margin: { left: 20, right: 20 },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;

  // Liabilities
  const liabilityRows: (string | number)[][] = [];
  for (const l of data.liabilities.byType) {
    const label = l.type.charAt(0).toUpperCase() + l.type.slice(1).replace('_', ' ');
    liabilityRows.push([`  ${label} (${l.count})`, formatCurrency(l.balance)]);
  }
  liabilityRows.push([{ content: 'Total Liabilities', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.liabilities.totalLiabilities), styles: { fontStyle: 'bold' } } as unknown as string]);

  autoTable(doc, {
    startY: y,
    head: [['Liabilities', 'Amount']],
    body: liabilityRows.length > 1 ? liabilityRows : [['  No outstanding liabilities', '$0.00']],
    theme: 'plain',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { fontSize: 9, cellPadding: 2 },
    margin: { left: 20, right: 20 },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;

  // Equity
  autoTable(doc, {
    startY: y,
    head: [["Owner's Equity", 'Amount']],
    body: [
      ["  Owner's Equity", formatCurrency(data.equity.ownersEquity)],
      [{ content: 'TOTAL LIABILITIES & EQUITY', styles: { fontStyle: 'bold', fontSize: 10 } } as unknown as string, { content: formatCurrency(data.totalLiabilitiesAndEquity), styles: { fontStyle: 'bold', fontSize: 10 } } as unknown as string],
    ],
    theme: 'plain',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { fontSize: 9, cellPadding: 2 },
    margin: { left: 20, right: 20 },
  });

  addFooter(doc);
  doc.save(`Balance_Sheet_${data.asOfDate}.pdf`);
}

// ============================================
// Cash Flow Statement
// ============================================

export interface CashFlowData {
  period: { from: string; to: string };
  operating: {
    cashReceived: number;
    paymentCount: number;
    expensesPaid: number;
    expenseCount: number;
    taxPayments: number;
    netOperating: number;
  };
  investing: {
    purchases: { type: string; count: number; amount: number }[];
    totalPurchases: number;
    disposalProceeds: number;
    netInvesting: number;
  };
  financing: {
    loanPayments: number;
    netFinancing: number;
  };
  netCashFlow: number;
  monthlyBreakdown: { month: string; monthName: string; cashIn: number; cashOut: number; netFlow: number }[];
}

export function exportCashFlowPDF(data: CashFlowData, businessName?: string): void {
  const doc = new jsPDF();
  const fromDate = new Date(data.period.from).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const toDate = new Date(data.period.to).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  let y = addHeader(doc, 'Statement of Cash Flows', `For the Period ${fromDate} to ${toDate}`, businessName);

  // Operating Activities
  autoTable(doc, {
    startY: y,
    head: [['Operating Activities', 'Amount']],
    body: [
      [`  Cash Received from Customers (${data.operating.paymentCount} payments)`, formatCurrency(data.operating.cashReceived)],
      [`  Cash Paid for Expenses (${data.operating.expenseCount} expenses)`, `(${formatCurrency(data.operating.expensesPaid)})`],
      ['  Estimated Tax Payments', `(${formatCurrency(data.operating.taxPayments)})`],
      [{ content: 'Net Cash from Operating Activities', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.operating.netOperating), styles: { fontStyle: 'bold', textColor: data.operating.netOperating >= 0 ? COLORS.accent : COLORS.danger } } as unknown as string],
    ],
    theme: 'plain',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { fontSize: 9, cellPadding: 2 },
    margin: { left: 20, right: 20 },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;

  // Investing Activities
  const investRows: (string | number)[][] = [];
  for (const p of data.investing.purchases) {
    const label = p.type.charAt(0).toUpperCase() + p.type.slice(1).replace('_', ' ');
    investRows.push([`  ${label} Purchases (${p.count})`, `(${formatCurrency(p.amount)})`]);
  }
  if (data.investing.disposalProceeds > 0) {
    investRows.push(['  Proceeds from Asset Disposals', formatCurrency(data.investing.disposalProceeds)]);
  }
  if (investRows.length === 0) {
    investRows.push(['  No investing activity', '$0.00']);
  }
  investRows.push([{ content: 'Net Cash from Investing Activities', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.investing.netInvesting), styles: { fontStyle: 'bold' } } as unknown as string]);

  autoTable(doc, {
    startY: y,
    head: [['Investing Activities', 'Amount']],
    body: investRows,
    theme: 'plain',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { fontSize: 9, cellPadding: 2 },
    margin: { left: 20, right: 20 },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;

  // Financing Activities
  autoTable(doc, {
    startY: y,
    head: [['Financing Activities', 'Amount']],
    body: [
      ['  Loan Interest Payments', `(${formatCurrency(data.financing.loanPayments)})`],
      [{ content: 'Net Cash from Financing Activities', styles: { fontStyle: 'bold' } } as unknown as string, { content: formatCurrency(data.financing.netFinancing), styles: { fontStyle: 'bold' } } as unknown as string],
    ],
    theme: 'plain',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 10 },
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { fontSize: 9, cellPadding: 2 },
    margin: { left: 20, right: 20 },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;

  // Net Cash Flow
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(1);
  doc.line(20, y, doc.internal.pageSize.getWidth() - 20, y);
  y += 2;

  autoTable(doc, {
    startY: y,
    body: [
      [
        { content: 'Net Change in Cash', styles: { fontStyle: 'bold', fontSize: 11 } } as unknown as string,
        { content: formatCurrency(data.netCashFlow), styles: { fontStyle: 'bold', fontSize: 11, textColor: data.netCashFlow >= 0 ? COLORS.accent : COLORS.danger } } as unknown as string,
      ],
    ],
    theme: 'plain',
    columnStyles: { 0: { cellWidth: 120 }, 1: { halign: 'right', cellWidth: 50 } },
    styles: { cellPadding: 3 },
    margin: { left: 20, right: 20 },
  });

  addFooter(doc);
  doc.save(`Cash_Flow_Statement_${data.period.from}_to_${data.period.to}.pdf`);
}

// ============================================
// Multi-Year Forecast
// ============================================

export interface ForecastHistoryItem {
  year: number;
  revenue: number;
  expenses: number;
  subscriptionCosts: number;
  capitalExpenditures: number;
  netIncome: number;
}

export interface ForecastProjection {
  year: number;
  revenue: number;
  expenses: number;
  capitalInvestment: number;
  totalDeductions: number;
  totalTax: number;
  effectiveRate: number;
  netIncomeAfterTax: number;
}

export interface ForecastPDFConfig {
  revenueGrowthRates: number[];
  expenseGrowthRates: number[];
  capitalInvestments: number[];
  filingStatus: string;
  entityType: string;
  state: string;
}

export function exportForecastPDF(
  history: ForecastHistoryItem[],
  projections: ForecastProjection[],
  config: ForecastPDFConfig,
  baseYear: number,
  businessName?: string,
): void {
  const doc = new jsPDF({ orientation: 'landscape' });
  const lastYear = projections[projections.length - 1]?.year || baseYear + 4;
  let y = addHeader(doc, 'Multi-Year Financial Forecast', `${baseYear + 1} \u2013 ${lastYear} Projections`, businessName);

  // Assumptions summary
  const assumptionRows: string[][] = [];
  for (let i = 0; i < projections.length; i++) {
    assumptionRows.push([
      (baseYear + i + 1).toString(),
      `${config.revenueGrowthRates[i] ?? 10}%`,
      `${config.expenseGrowthRates[i] ?? 5}%`,
      config.capitalInvestments[i] ? formatCurrency(config.capitalInvestments[i]) : '$0',
    ]);
  }

  autoTable(doc, {
    startY: y,
    head: [['Year', 'Revenue Growth', 'Expense Growth', 'Capital Investment']],
    body: assumptionRows,
    theme: 'grid',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 9 },
    styles: { fontSize: 9, cellPadding: 2, halign: 'center' },
    margin: { left: 20, right: 20 },
    tableWidth: 'auto',
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

  // Main projection table
  const headers = ['Metric'];
  if (history.length > 0) {
    headers.push(`${baseYear} (Actual)`);
  }
  for (const p of projections) {
    headers.push(p.year.toString());
  }

  const latestActual = history[history.length - 1];
  const baseExpenses = latestActual ? latestActual.expenses + latestActual.subscriptionCosts : 0;

  const makeRow = (label: string, actualVal: number | null, projGetter: (p: ForecastProjection) => number): string[] => {
    const row = [label];
    if (history.length > 0) {
      row.push(actualVal !== null ? formatCurrency(actualVal) : '\u2014');
    }
    for (const p of projections) {
      row.push(formatCurrency(projGetter(p)));
    }
    return row;
  };

  const bodyRows = [
    makeRow('Revenue', latestActual?.revenue ?? null, p => p.revenue),
    makeRow('Operating Expenses', baseExpenses || null, p => p.expenses),
    makeRow('Capital Investment', latestActual?.capitalExpenditures ?? null, p => p.capitalInvestment),
    makeRow('Total Deductions', null, p => p.totalDeductions),
    makeRow('Total Tax', null, p => p.totalTax),
    // Effective Rate row
    (() => {
      const row = ['Effective Tax Rate'];
      if (history.length > 0) row.push('\u2014');
      for (const p of projections) {
        row.push(`${(p.effectiveRate * 100).toFixed(1)}%`);
      }
      return row;
    })(),
    makeRow('Net Income (After Tax)', latestActual?.netIncome ?? null, p => p.netIncomeAfterTax),
    makeRow('Quarterly Tax Reserve', null, p => p.totalTax / 4),
  ];

  autoTable(doc, {
    startY: y,
    head: [headers],
    body: bodyRows,
    theme: 'grid',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 45 } },
    styles: { cellPadding: 3, halign: 'right', overflow: 'linebreak' },
    didParseCell: (data) => {
      // Bold the Net Income row
      if (data.row.index === bodyRows.length - 2 && data.section === 'body') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fontSize = 10;
      }
      // Red for tax rows
      if ((data.row.index === 4 || data.row.index === 7) && data.section === 'body' && data.column.index > 0) {
        data.cell.styles.textColor = COLORS.danger;
      }
      // Green for net income
      if (data.row.index === bodyRows.length - 2 && data.section === 'body' && data.column.index > 0) {
        const val = parseFloat(String(data.cell.raw).replace(/[$,()]/g, ''));
        if (!isNaN(val)) {
          data.cell.styles.textColor = val >= 0 ? COLORS.accent : COLORS.danger;
        }
      }
      // Left-align first column
      if (data.column.index === 0) {
        data.cell.styles.halign = 'left';
      }
    },
    margin: { left: 20, right: 20 },
  });

  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;

  // Footer note
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...COLORS.gray);
  doc.text(
    `Filing: ${config.filingStatus.replace(/_/g, ' ')} | Entity: ${config.entityType.replace(/_/g, ' ')}${config.state ? ` | State: ${config.state}` : ''}`,
    20, y,
  );
  doc.text(
    'Tax projections use 2025 federal brackets and are estimates only. Consult a tax professional.',
    20, y + 5,
  );

  addFooter(doc);
  doc.save(`Forecast_${baseYear + 1}_to_${lastYear}.pdf`);
}
